import { createContext, useContext, useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import Loading from '@/components/Loading'

export type OrganizationRole = 'admin' | 'coordinator' | 'viewer'

// Response shape of GET /provider/me.
export interface IProviderSession {
    user: {
        name: string | null
        email: string
    }
    organization: {
        name: string
        displayName: string
        contactName: string | null
        contactEmail: string | null
        seatQuota: number
        type: string
    }
    role: OrganizationRole
}

const ProviderSessionContext = createContext<IProviderSession | null>(null)

// The signed-in coordinator's org context. Only non-null inside
// ProviderAuthWrapper, which blocks rendering until the session has loaded.
export const useProviderSession = () => useContext(ProviderSessionContext)

// Provider portals surface family data, so an unattended session should not stay
// open indefinitely. Log out after this much inactivity; the backend still caps
// the absolute session via the refresh-cookie lifetime.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000

// Logs the provider out after IDLE_TIMEOUT_MS with no user interaction. Any of the
// tracked events resets the countdown. On timeout, clearUser() drops the in-memory
// token and flips auth state to unauthenticated, which bounces the wrapper to /login.
function useIdleLogout(active: boolean, onIdle: () => void) {
    useEffect(() => {
        if (!active) return

        let timer: ReturnType<typeof setTimeout>
        const reset = () => {
            clearTimeout(timer)
            timer = setTimeout(onIdle, IDLE_TIMEOUT_MS)
        }

        const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart']
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }))
        reset()

        return () => {
            clearTimeout(timer)
            events.forEach((e) => window.removeEventListener(e, reset))
        }
    }, [active, onIdle])
}

// Gates every authenticated provider-portal route. Reuses the family app's
// token/refresh machinery (AuthProvider), then requires an organization
// membership via /provider/me — a signed-in account without one (family user,
// org-less platform admin) is bounced to the portal login.
export function ProviderAuthWrapper() {
    const { user, checkAuth, clearUser, state } = useAuth()
    const [session, setSession] = useState<IProviderSession | null>(null)
    const [denied, setDenied] = useState(false)

    useEffect(() => {
        checkAuth()
    }, [user])

    // Idle auto-logout while an authenticated provider session is mounted.
    useIdleLogout(state === 'authenticated', clearUser)

    useEffect(() => {
        if (state !== 'authenticated' || session) return
        // suppressForbiddenRedirect: a 403 here means "no org membership" and
        // is handled with a redirect to the portal login, not /forbidden.
        api<IProviderSession>(ROUTES.provider.me, undefined, undefined, undefined, true).then((res) => {
            if (res.data?.organization) {
                setSession(res.data as IProviderSession)
            } else {
                setDenied(true)
            }
        })
    }, [state])

    if (state === 'unauthenticated' || denied) return <Navigate to='/login' replace />
    if (!session) return <Loading mode='block' />

    return (
        <ProviderSessionContext.Provider value={session}>
            <Outlet />
        </ProviderSessionContext.Provider>
    )
}
